import React, { useState } from 'react';
import axios from 'axios';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CustomIdeaPage() {
  const { config } = useSiteConfig();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    budget: '',
    needed_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/custom-ideas`, formData);
      setSubmitted(true);
      toast.success('Idea enviada correctamente');
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: '',
        budget: '',
        needed_date: ''
      });
    } catch (error) {
      console.error('Error submitting idea:', error);
      toast.error('Error al enviar la idea');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-[#374963] mb-4" data-testid="custom-idea-page-title">
            {config?.custom_idea_title || '¿Tienes una idea? Nosotros la hacemos realidad'}
          </h1>
          <p className="text-lg text-[#73706e] leading-relaxed">
            {config?.custom_idea_subtitle || 'Si no encuentras lo que buscas en nuestra tienda, cuéntanos tu proyecto. Diseñamos y fabricamos piezas únicas utilizando impresión 3D y corte láser totalmente personalizadas.'}
          </p>
        </div>

        {submitted ? (
          <div className="bg-white p-12 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] text-center" data-testid="custom-idea-success-message">
            <div className="w-16 h-16 bg-[#785534] rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-[#374963] mb-4">¡Gracias!</h2>
            <p className="text-[#73706e] leading-relaxed mb-6">
              Hemos recibido tu idea. Revisaremos tu proyecto y te responderemos lo antes posible con una propuesta y un presupuesto.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="px-6 py-3 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors"
              data-testid="submit-another-idea-button"
            >
              Enviar otra idea
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="custom-idea-form">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#374963] mb-2">
                    Nombre *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="custom-idea-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#374963] mb-2">
                    Correo Electrónico *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="custom-idea-email-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#374963] mb-2">
                  Teléfono (opcional)
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                  data-testid="custom-idea-phone-input"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-[#374963] mb-2">
                  Descripción de la idea *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                  placeholder="Cuéntanos tu proyecto en detalle..."
                  data-testid="custom-idea-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-[#374963] mb-2">
                    Presupuesto aproximado (opcional)
                  </label>
                  <input
                    id="budget"
                    name="budget"
                    type="text"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="Ej: 50-100€"
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="custom-idea-budget-input"
                  />
                </div>

                <div>
                  <label htmlFor="needed_date" className="block text-sm font-medium text-[#374963] mb-2">
                    Fecha en la que lo necesitas (opcional)
                  </label>
                  <input
                    id="needed_date"
                    name="needed_date"
                    type="date"
                    value={formData.needed_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
                    data-testid="custom-idea-date-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                data-testid="custom-idea-submit-button"
              >
                {loading ? (
                  <span>Enviando...</span>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Enviar Proyecto</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CustomIdeaPage;